/**
 * Wires `loansConsign.model.ts` to the reads and writes behind the two forms.
 *
 * Calls `fetchStudioWorks` for the pickers and `fetchOwnerLoans` plus
 * `fetchArrangements` for the rail, all in one pass; writes are `proposeLoan`
 * and `offerConsignment`. Every submit re-reads the rail from its tables
 * rather than appending locally, because the row that renders must be the
 * row that landed — a loan the server opened `agreed` must not show as
 * `proposed` because the client assumed a question was asked.
 *
 * The state the model cannot hold is the active tab, the two drafts, the
 * shared refusal and confirmation lines, the working flag and the generation
 * counter. Switching tabs clears the refusal and confirmation, since each
 * belongs to the form that produced it.
 */

import { useEffect, useState } from "react";
import type { Arrangement, Loan, StudioWork } from "@ondo/shared";

import {
  ApiError,
  fetchArrangements,
  fetchOwnerLoans,
  fetchStudioWorks,
  offerConsignment,
  dispatchLoan,
  proposeLoan,
} from "../lib/api.ts";
import { LOANS_COPY } from "./estate.constants.ts";
import {
  EMPTY_CONSIGN,
  EMPTY_LOAN,
  consignRefusal,
  loanConfirmation,
  loanRefusal,
  toConsignRequest,
  toLoanRequest,
  toRail,
} from "./loansConsign.model.ts";
import type { ConsignDraft, LoanDraft, RailRow } from "./loansConsign.model.ts";

export type LoansTab = "loan" | "consign";

export type LoansView =
  | { kind: "loading"; message: string }
  | { kind: "error"; title: string; message: string }
  | {
      kind: "open";
      /** The owner's works, for both pickers. */
      works: StudioWork[];
      rail: RailRow[];
      railEmpty: string | null;
    };

/**
 * Two forms over one work list, and the rail that shows what each produced.
 *
 * Every submit re-reads the rail from its tables rather than appending
 * locally: the row that renders is the row that landed, statuses included —
 * a loan the server opened `agreed` must not render as `proposed` because the
 * client assumed a question was asked.
 */
export function useLoansConsignController() {
  const [tab, setTab] = useState<LoansTab>("loan");
  const [works, setWorks] = useState<StudioWork[] | null>(null);
  const [loans, setLoans] = useState<Loan[]>([]);
  const [arrangements, setArrangements] = useState<Arrangement[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [loanDraft, setLoanDraft] = useState<LoanDraft>(EMPTY_LOAN);
  const [consignDraft, setConsignDraft] = useState<ConsignDraft>(EMPTY_CONSIGN);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    let active = true;

    Promise.all([fetchStudioWorks(), fetchOwnerLoans(), fetchArrangements()])
      .then(([owned, ledger, out]) => {
        if (!active) return;
        setWorks(owned);
        setLoans(ledger);
        setArrangements(out);
      })
      .catch((cause: unknown) => {
        if (!active) return;
        setError(cause instanceof ApiError ? cause.message : LOANS_COPY.errorTitle);
      });

    return () => {
      active = false;
    };
  }, [generation]);

  const switchTab = (next: LoansTab) => {
    setTab(next);
    setRefusal(null);
    setConfirmation(null);
  };

  const submitLoan = async () => {
    const stop = loanRefusal(loanDraft);
    if (stop !== null) {
      setRefusal(stop);
      return;
    }

    setWorking(true);
    setRefusal(null);
    try {
      const loan = await proposeLoan(loanDraft.workId, toLoanRequest(loanDraft));
      setConfirmation(loanConfirmation(loan));
      setLoanDraft(EMPTY_LOAN);
      setGeneration((n) => n + 1);
    } catch (cause) {
      setRefusal(cause instanceof ApiError ? cause.message : LOANS_COPY.errorTitle);
    } finally {
      setWorking(false);
    }
  };

  /**
   * The lender sends the work out.
   *
   * The third write on this screen and the simplest: no draft, no validation,
   * one id. `ondo.mark_loan_out()` admits 'agreed' and the owner, and the rail
   * offers the control on exactly that set — so a refusal here means the row
   * moved while the screen was open, and the server's own sentence says which
   * rule refused it.
   *
   * The re-read follows, as it does after both forms: the row that renders is
   * the row that landed, and its state and its controls both change with it.
   */
  const dispatch = async (loanId: string) => {
    setWorking(true);
    setRefusal(null);
    try {
      await dispatchLoan(loanId);
      setGeneration((n) => n + 1);
    } catch (cause) {
      setRefusal(cause instanceof ApiError ? cause.message : LOANS_COPY.errorTitle);
    } finally {
      setWorking(false);
    }
  };

  const submitConsign = async () => {
    const stop = consignRefusal(consignDraft);
    if (stop !== null) {
      setRefusal(stop);
      return;
    }

    const title =
      works?.find((work) => work.workId === consignDraft.workId)?.title ?? "";

    setWorking(true);
    setRefusal(null);
    try {
      await offerConsignment(consignDraft.workId, toConsignRequest(consignDraft));
      setConfirmation(LOANS_COPY.consignOffered(title));
      setConsignDraft(EMPTY_CONSIGN);
      setGeneration((n) => n + 1);
    } catch (cause) {
      setRefusal(cause instanceof ApiError ? cause.message : LOANS_COPY.errorTitle);
    } finally {
      setWorking(false);
    }
  };

  const rail = toRail(loans, arrangements);
  const view: LoansView = error
    ? { kind: "error", title: LOANS_COPY.errorTitle, message: error }
    : works === null
      ? { kind: "loading", message: LOANS_COPY.loading }
      : {
          kind: "open",
          works,
          rail,
          railEmpty: rail.length === 0 ? LOANS_COPY.railEmpty : null,
        };

  return {
    view,
    tab,
    switchTab,
    loanDraft,
    setLoanDraft: (patch: Partial<LoanDraft>) => {
      setLoanDraft((draft) => ({ ...draft, ...patch }));
      setRefusal(null);
    },
    consignDraft,
    setConsignDraft: (patch: Partial<ConsignDraft>) => {
      setConsignDraft((draft) => ({ ...draft, ...patch }));
      setRefusal(null);
    },
    refusal,
    confirmation,
    working,
    dispatch,
    submitLoan,
    submitConsign,
  };
}
